import React from 'react'
import { useTranslation } from 'react-i18next'
import AppBar from '@material-ui/core/AppBar'
import Checkbox from '@material-ui/core/Checkbox'
import CloseIcon from '@material-ui/icons/Close'
import Dialog from '@material-ui/core/Dialog'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import FormControl from '@material-ui/core/FormControl'
import FormLabel from '@material-ui/core/FormLabel'
import IconButton from '@material-ui/core/IconButton'
import Radio from '@material-ui/core/Radio'
import RadioGroup from '@material-ui/core/RadioGroup'
import Switch from '@material-ui/core/Switch'
import Toolbar from '@material-ui/core/Toolbar'
import Typography from '@material-ui/core/Typography'
import 'react-perfect-scrollbar/dist/css/styles.css'
import PerfectScrollbar from 'react-perfect-scrollbar'
import {
  getSettingsSaveAs,
  setSettingsSaveAs,
  getSettingsSaveInto,
  setSettingsSaveInto,
  getSettingsCaptureDest,
  setSettingsCaptureDest,
  getSettingsOfferShareAfterCapture,
  setSettingsOfferShareAfterCapture,
  getSettingsDcmHeader,
  setSettingsDcmHeader,
  getSettingsOverlay,
  setSettingsOverlay,
  getSettingsFsView,
  setSettingsFsView,
  getSettingsDicomdirView,
  setSettingsDicomdirView,
  getSettingsMprInterpolation,
  setSettingsMprInterpolation,
} from '../functions'
import { getAppLanguage, setAppLanguage } from '../i18n'
import {
  getJoystickSlots,
  setJoystickSlots,
  JOYSTICK_ACTION_IDS,
  JOYSTICK_MAX_SLOTS,
  DEFAULT_JOYSTICK_SLOTS,
} from '../utils/joystickPrefs'
import Button from '@material-ui/core/Button'
import { ACTION_META } from './JoystickMenu'
import ScpDestinationsDlg from './ScpDestinationsDlg'
import PlanFeaturesPanel from './PlanFeaturesPanel'
import { isDeveloperMode } from '../plan'
import { RENDER_MODE, isModeViableForCaps } from '../utils/renderMode'
import {
  getScrollFeelPrefs,
  setScrollFeelPreset,
  detectScrollDevice,
} from '../utils/scrollFeelPrefs'
import {
  getExplorerAutoRefPin,
  setExplorerAutoRefPin,
} from '../utils/explorerDownloadPrefs'
import {
  getR2ZipRangeMode,
  setR2ZipRangeMode,
  R2_ZIP_RANGE_MODE,
} from '../utils/r2ZipDownloadPrefs'
import '../skins/settings.css'

/**
 * @param {{
 *   onClose: () => void,
 *   onPlanChanged?: () => void,
 *   skin?: 'classic'|'edge'|'studio',
 *   isPortalProfile?: boolean,
 *   renderMode?: string,
 *   manifestMediaCaps?: { hasPreview?: boolean, hasDicom?: boolean }|null,
 *   onSetRenderMode?: (mode: string) => void,
 *   onExplorerAutoRefPinChange?: () => void,
 * }} props
 */
const Settings = ({
  onClose,
  onPlanChanged,
  skin = 'classic',
  isPortalProfile = false,
  renderMode,
  manifestMediaCaps = null,
  onSetRenderMode,
  onExplorerAutoRefPinChange,
}) => {
  const { t, i18n } = useTranslation()
  const skinKey = skin === 'studio' || skin === 'edge' ? skin : 'classic'

  let saveAs = getSettingsSaveAs()
  let saveInto = getSettingsSaveInto()
  let captureDest = getSettingsCaptureDest()
  let offerShareAfterCapture = getSettingsOfferShareAfterCapture()
  let exportAs = getSettingsDcmHeader()
  let overlay = getSettingsOverlay()
  let fsView = getSettingsFsView()
  let dicomdirView = getSettingsDicomdirView()
  let mprInterpolation = getSettingsMprInterpolation()

  const handleChangeSaveAs = (event) => {
    setState({ ...state, saveAs: event.target.value })
    setSettingsSaveAs(event.target.value)
  }

  const handleChangeSaveInto = (event) => {
    setState({ ...state, saveInto: event.target.value })
    setSettingsSaveInto(event.target.value)
  }

  const handleChangeCaptureDest = (event) => {
    setState({ ...state, captureDest: event.target.value })
    setSettingsCaptureDest(event.target.value)
  }

  const handleChangeOfferShareAfterCapture = (event) => {
    const checked = event.target.checked
    setState({ ...state, offerShareAfterCapture: checked })
    setSettingsOfferShareAfterCapture(checked)
  }

  const handleChangeExportAs = (event) => {
    setState({ ...state, exportAs: event.target.value })
    setSettingsDcmHeader(event.target.value)
  }

  const handleChangeOverlay = (event) => {
    setState({ ...state, overlay: event.target.checked })
    setSettingsOverlay(event.target.checked)
  }

  const handleChangeFsView = (event) => {
    setState({ ...state, fsView: event.target.value })
    setSettingsFsView(event.target.value)
  }

  const handleChangeDicomdirView = (event) => {
    setState({ ...state, dicomdirView: event.target.value })
    setSettingsDicomdirView(event.target.value)
  }

  const handleChangeMprInterpolation = (event) => {
    setState({ ...state, mprInterpolation: event.target.value })
    setSettingsMprInterpolation(event.target.value)
  }

  const handleChangeLanguage = (event) => {
    const lng = event.target.value
    setState({ ...state, language: lng })
    setAppLanguage(lng)
  }

  const [visibleScpDlg, setVisibleScpDlg] = React.useState(false)

  const [state, setState] = React.useState({
    saveAs: saveAs,
    saveInto: saveInto,
    captureDest: captureDest,
    offerShareAfterCapture: offerShareAfterCapture,
    exportAs: exportAs,
    overlay: overlay,
    fsView: fsView,
    dicomdirView: dicomdirView,
    mprInterpolation: mprInterpolation,
    language: getAppLanguage(),
    joystickSlots: getJoystickSlots(),
    scrollFeelPreset: getScrollFeelPrefs().preset,
    explorerAutoRefPin: getExplorerAutoRefPin({ portalProfile: isPortalProfile }),
    r2ZipRangeMode: getR2ZipRangeMode(),
  })

  const handleJoystickSlotToggle = (actionId) => {
    setState((prev) => {
      const cur = Array.isArray(prev.joystickSlots) ? prev.joystickSlots.slice() : []
      const idx = cur.indexOf(actionId)
      let next
      if (idx >= 0) {
        next = cur.filter((id) => id !== actionId)
      } else if (cur.length >= JOYSTICK_MAX_SLOTS) {
        next = cur
      } else {
        next = cur.concat([actionId])
      }
      if (!next.length) next = DEFAULT_JOYSTICK_SLOTS.slice()
      setJoystickSlots(next)
      return { ...prev, joystickSlots: next }
    })
  }

  React.useEffect(() => {
    setState((s) => ({ ...s, language: getAppLanguage() }))
  }, [i18n.language])

  const section = (label, children) => (
    <div className="udv-settings__section">
      {label ? (
        <FormLabel component="legend" className="udv-settings__section-label">
          {label}
        </FormLabel>
      ) : null}
      {children}
    </div>
  )

  return (
    <Dialog
      fullScreen
      open={true}
      onClose={onClose}
      className="udv-settings-dlg"
      PaperProps={{
        className: `udv-settings udv-settings--${skinKey}`,
        style: { background: 'transparent' },
      }}
    >
      <div className={`udv-settings udv-settings--${skinKey}`}>
        <AppBar className="udv-settings__bar" position="relative" elevation={0}>
          <Toolbar variant="dense">
            <IconButton
              edge="start"
              color="inherit"
              onClick={onClose}
              aria-label={t('common.close')}
            >
              <CloseIcon />
            </IconButton>
            <Typography variant="h6" className="udv-settings__title">
              {t('settings.title')}
            </Typography>
          </Toolbar>
        </AppBar>
        <PerfectScrollbar>
          <div className="udv-settings__body">
            {section(
              t('lang.label'),
              <FormControl component="fieldset">
                <RadioGroup
                  aria-label="language"
                  name="language"
                  value={state.language}
                  onChange={handleChangeLanguage}
                >
                  <FormControlLabel
                    value="es"
                    control={<Radio size="small" />}
                    label={t('lang.es')}
                  />
                  <FormControlLabel
                    value="en"
                    control={<Radio size="small" />}
                    label={t('lang.en')}
                  />
                </RadioGroup>
              </FormControl>
            )}

            {section(
              t('joystick.settingsLabel'),
              <>
                <Typography
                  variant="caption"
                  style={{ display: 'block', marginBottom: 8 }}
                >
                  {t('joystick.settingsHint')}
                </Typography>
                {JOYSTICK_ACTION_IDS.map((id) => {
                  const meta = ACTION_META[id] || {}
                  const checked = (state.joystickSlots || []).indexOf(id) !== -1
                  return (
                    <FormControlLabel
                      key={id}
                      control={
                        <Checkbox
                          checked={checked}
                          onChange={() => handleJoystickSlotToggle(id)}
                          size="small"
                          disabled={
                            !checked &&
                            (state.joystickSlots || []).length >= JOYSTICK_MAX_SLOTS
                          }
                        />
                      }
                      label={t(meta.labelKey || id)}
                    />
                  )
                })}
              </>
            )}

            {isPortalProfile
              ? section(
                  t('settings.renderModePortal'),
                  <Typography variant="caption" style={{ display: 'block' }}>
                    {t('settings.renderModePortalStrict')}
                  </Typography>
                )
              : null}

            {section(
              null,
              <FormControlLabel
                control={
                  <Checkbox
                    checked={state.overlay}
                    onChange={handleChangeOverlay}
                    value="overlay"
                    size="small"
                  />
                }
                label={t('settings.showOverlay')}
              />
            )}

            {section(
              t('settings.fsViewFrom'),
              <RadioGroup
                aria-label="filesystem"
                name="filesystem"
                value={state.fsView}
                onChange={handleChangeFsView}
              >
                <FormControlLabel
                  value="left"
                  control={<Radio size="small" />}
                  label={t('common.left')}
                />
                <FormControlLabel
                  value="right"
                  control={<Radio size="small" />}
                  label={t('common.right')}
                />
                <FormControlLabel
                  value="bottom"
                  control={<Radio size="small" />}
                  label={t('common.bottom')}
                />
              </RadioGroup>
            )}

            {section(
              t('settings.dicomdirViewFrom'),
              <RadioGroup
                aria-label="dicomdir"
                name="dicomdir"
                value={state.dicomdirView}
                onChange={handleChangeDicomdirView}
              >
                <FormControlLabel
                  value="left"
                  control={<Radio size="small" />}
                  label={t('common.left')}
                />
                <FormControlLabel
                  value="right"
                  control={<Radio size="small" />}
                  label={t('common.right')}
                />
                <FormControlLabel
                  value="bottom"
                  control={<Radio size="small" />}
                  label={t('common.bottom')}
                />
              </RadioGroup>
            )}

            {section(
              t('settings.captureDest'),
              <RadioGroup
                aria-label="capturedest"
                name="capturedest"
                value={state.captureDest}
                onChange={handleChangeCaptureDest}
              >
                <FormControlLabel
                  value="ask"
                  control={<Radio size="small" />}
                  label={t('settings.captureDestAsk')}
                />
                <FormControlLabel
                  value="clipboard"
                  control={<Radio size="small" />}
                  label={t('settings.captureDestClipboard')}
                />
                <FormControlLabel
                  value="file"
                  control={<Radio size="small" />}
                  label={t('settings.captureDestFile')}
                />
              </RadioGroup>
            )}

            {section(
              null,
              <>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={!!state.offerShareAfterCapture}
                      onChange={handleChangeOfferShareAfterCapture}
                      size="small"
                    />
                  }
                  label={t('settings.offerShareAfterCapture')}
                />
                <Typography
                  variant="caption"
                  style={{ display: 'block', marginTop: 4 }}
                >
                  {t('settings.zipShareHint')}
                </Typography>
              </>
            )}

            {section(
              t('settings.saveScreenshotAs'),
              <RadioGroup
                aria-label="saveas"
                name="saveas"
                value={state.saveAs}
                onChange={handleChangeSaveAs}
              >
                <FormControlLabel
                  value="jpeg"
                  control={<Radio size="small" />}
                  label="JPEG"
                />
                <FormControlLabel
                  value="png"
                  control={<Radio size="small" />}
                  label="PNG"
                />
              </RadioGroup>
            )}

            {section(
              t('settings.saveScreenshotInto'),
              <RadioGroup
                aria-label="saveinto"
                name="saveinto"
                value={state.saveInto}
                onChange={handleChangeSaveInto}
              >
                <FormControlLabel
                  value="local"
                  control={<Radio size="small" />}
                  label={t('settings.localFs')}
                />
                <FormControlLabel
                  value="sandbox"
                  control={<Radio size="small" />}
                  label={t('settings.sandboxFs')}
                />
              </RadioGroup>
            )}

            {section(
              t('settings.exportHeaderAs'),
              <RadioGroup
                aria-label="exportas"
                name="exportas"
                value={state.exportAs}
                onChange={handleChangeExportAs}
              >
                <FormControlLabel
                  value="json"
                  control={<Radio size="small" />}
                  label="JSON"
                />
                <FormControlLabel
                  value="csv"
                  control={<Radio size="small" />}
                  label="CSV"
                />
              </RadioGroup>
            )}

            {section(
              t('settings.scpTitle'),
              <>
                <Typography
                  variant="caption"
                  style={{ display: 'block', marginBottom: 8 }}
                >
                  {t('settings.scpHint')}
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setVisibleScpDlg(true)}
                >
                  {t('settings.scpManage')}
                </Button>
              </>
            )}

            {section(
              t('settings.mprInterpolation'),
              <RadioGroup
                aria-label="mprinterpolation"
                name="mprinterpolation"
                value={state.mprInterpolation}
                onChange={handleChangeMprInterpolation}
              >
                <FormControlLabel
                  value="no"
                  control={<Radio size="small" />}
                  label={t('settings.mprNoInterp')}
                />
                <FormControlLabel
                  value="weightedlinear"
                  control={<Radio size="small" />}
                  label={t('settings.mprWeighted')}
                />
              </RadioGroup>
            )}

            {isDeveloperMode() ? (
              section(
                t('settings.scrollFeel'),
                <>
                  <Typography
                    variant="caption"
                    style={{ display: 'block', marginBottom: 8 }}
                  >
                    {t('settings.scrollFeelHint', {
                      device: t(`settings.scrollDevice.${detectScrollDevice()}`),
                    })}
                  </Typography>
                  <RadioGroup
                    aria-label="scrollfeel"
                    name="scrollfeel"
                    value={state.scrollFeelPreset}
                    onChange={(event) => {
                      const preset = setScrollFeelPreset(event.target.value)
                      setState({ ...state, scrollFeelPreset: preset })
                    }}
                  >
                    <FormControlLabel
                      value="precise"
                      control={<Radio size="small" />}
                      label={t('settings.scrollFeelPrecise')}
                    />
                    <FormControlLabel
                      value="balanced"
                      control={<Radio size="small" />}
                      label={t('settings.scrollFeelBalanced')}
                    />
                    <FormControlLabel
                      value="fluid"
                      control={<Radio size="small" />}
                      label={t('settings.scrollFeelFluid')}
                    />
                  </RadioGroup>
                </>
              )
            ) : null}

            {!isPortalProfile ? (
              section(
                t('settings.explorerDownload'),
                <>
                  <Typography
                    variant="caption"
                    style={{ display: 'block', marginBottom: 8 }}
                  >
                    {t('settings.explorerAutoRefPinHint')}
                  </Typography>
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        color="primary"
                        checked={!!state.explorerAutoRefPin}
                        onChange={(e) => {
                          const enabled = setExplorerAutoRefPin(e.target.checked)
                          setState({ ...state, explorerAutoRefPin: enabled })
                          if (typeof onExplorerAutoRefPinChange === 'function') {
                            onExplorerAutoRefPinChange(enabled)
                          }
                        }}
                      />
                    }
                    label={t('settings.explorerAutoRefPin')}
                  />
                  <Typography
                    variant="caption"
                    style={{ display: 'block', marginTop: 16, marginBottom: 8 }}
                  >
                    {t('settings.r2ZipRangeModeHint')}
                  </Typography>
                  <FormControl component="fieldset">
                    <FormLabel component="legend" style={{ fontSize: 13 }}>
                      {t('settings.r2ZipRangeMode')}
                    </FormLabel>
                    <RadioGroup
                      row
                      name="r2-zip-range-mode"
                      value={state.r2ZipRangeMode || R2_ZIP_RANGE_MODE.LEGACY}
                      onChange={(e) => {
                        const mode = setR2ZipRangeMode(e.target.value)
                        setState({ ...state, r2ZipRangeMode: mode })
                      }}
                    >
                      <FormControlLabel
                        value={R2_ZIP_RANGE_MODE.COALESCING}
                        control={<Radio size="small" color="primary" />}
                        label={t('settings.r2ZipRangeModeCoalescing')}
                      />
                      <FormControlLabel
                        value={R2_ZIP_RANGE_MODE.LEGACY}
                        control={<Radio size="small" color="primary" />}
                        label={t('settings.r2ZipRangeModeLegacy')}
                      />
                    </RadioGroup>
                  </FormControl>
                </>
              )
            ) : null}

            {isDeveloperMode() ? (
              <PlanFeaturesPanel onChanged={onPlanChanged} skin={skinKey} />
            ) : null}
          </div>
        </PerfectScrollbar>
      </div>
      <ScpDestinationsDlg
        open={visibleScpDlg}
        onClose={() => setVisibleScpDlg(false)}
      />
    </Dialog>
  )
}

export default Settings
